/**
 * @fileoverview Componente de detalle de producto para selección modal
 * @module ProductDetail
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useEffect, useState, memo
} from 'react';
import { isEmpty, truncate } from 'lodash';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../../util/numberUtils';
import { hasDiscount } from '../../../../util/productUtils';
import ProductDescription from './ProductDescription';
import ProductVariantsComponent from './ProductVariantsComponent';

/**
 * @typedef {Object} Product
 * @description Objeto producto con información completa
 * @property {string} id - Identificador único del producto
 * @property {string} web_id - ID web del producto
 * @property {string} title - Título del producto
 * @property {string} description - Descripción del producto
 * @property {number} price - Precio base del producto
 * @property {string} currency - Código de moneda
 * @property {number} quantity - Stock disponible
 * @property {Array} image_urls - URLs de imágenes del producto
 * @property {Array} product_variant_combinations - Variantes del producto
 * @property {Array} product_variants - Opciones de variantes
 * @property {boolean} active - Estado activo del producto
 */

/**
 * Componente Molecule para mostrar detalle completo de un producto.
 *
 * Implementa el patrón **Molecule** de Atomic Design con funcionalidad
 * completa para visualizar y seleccionar variantes de productos.
 *
 * @component ProductDetail
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Vista detallada del producto con imagen principal
 * - Información de precio, stock y descripción
 * - Selector de variantes si están disponibles
 * - Galería de imágenes del producto
 * - Botones de acción para envío
 * - Diseño responsivo con Tailwind CSS
 * - Optimización con React.memo
 *
 * **Atomic Design:**
 * - **Nivel**: Molecule
 * - **Composición**: Utiliza ProductDescription y ProductVariantsComponent
 * - **Dependencias**: Componentes compartidos, hooks de React
 *
 * @param {Object} props - Propiedades del componente
 * @param {Product} props.product - Producto a mostrar en detalle
 * @param {Product|null} props.selectedProductToSend - Producto seleccionado para envío
 * @param {function} props.setSelectedProductToSend - Función para actualizar producto seleccionado
 * @param {function} props.onBack - Callback ejecutado al presionar botón atrás
 * @param {function} [props.onSendCollection] - Callback para enviar colección de imágenes
 *
 * @returns {JSX.Element} Elemento JSX renderizado con detalle del producto
 *
 * @example
 * ```jsx
 * <ProductDetail
 *   product={selectedProduct}
 *   selectedProductToSend={selectedProductToSend}
 *   setSelectedProductToSend={setSelectedProductToSend}
 * />
 * ```
 */
const ProductDetail = memo(({
  product,
  selectedProductToSend,
  setSelectedProductToSend
}) => {
  const { t } = useTranslation('Products');

  // Estado local solo para la imagen seleccionada
  const [selectedImage, setSelectedImage] = useState({});

  /**
   * Inicializa el producto seleccionado para envío.
   */
  useEffect(() => {
    if (isEmpty(product)) return;

    const { product_variant_combinations } = product;

    // Si no tiene variantes, usar el producto base
    if (isEmpty(product_variant_combinations)) {
      setSelectedProductToSend(product);
    }
  }, [product, setSelectedProductToSend]);

  /**
   * Actualiza la imagen seleccionada según el producto.
   */
  useEffect(() => {
    if (isEmpty(selectedProductToSend)) {
      setSelectedImage({});
      return;
    }

    const isValidImage = (img) => img && !(typeof img === 'object' && Object.keys(img).length === 0);

    let img = {};

    if (selectedProductToSend?.is_combination) {
      // Es una combinación de variantes
      img = isValidImage(selectedProductToSend.attached_image)
        ? selectedProductToSend.attached_image
        : selectedProductToSend.product_image || {};
    } else {
      // Es el producto base
      img = selectedProductToSend.image_urls?.[0] || {};
    }

    setSelectedImage(img);
  }, [selectedProductToSend]);

  if (isEmpty(product)) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
        <p className="tw-text-gray-500">
          {t('productDetail.error.noProduct')}
        </p>
      </div>
    );
  }

  return (
    <div className="tw-relative tw-bg-white" style={{ height: 'calc(100vh - 62px)' }}>
      <div style={{ height: 'calc(100vh - 125px)', overflowY: 'auto' }}>
        <div className="tw-pb-5">
          <div>
            {/* Imagen principal del producto */}
            {!isEmpty(selectedImage) ? (
              <div className="tw-bg-gray-100 tw-border-2 tw-border-gray-200 tw-rounded-lg">
                <img
                  src={selectedImage.url}
                  referrerPolicy="no-referrer"
                  alt={product.title}
                  className="tw-rounded-lg tw-object-cover tw-w-full"
                  style={{ maxHeight: '140px' }}
                />
              </div>
            ) : (
              <div
                className="tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-w-full"
                style={{ height: '140px' }}
              >
                <i className="mi-image-outline tw-text-xl tw-text-blue-500"></i>
              </div>
            )}

            {/* Información del producto */}
            <div className="tw-flex tw-flex-col tw-gap-1.5 tw-mt-2">
              <span className="tw-font-semibold tw-text-sm tw-text-gray-950">
                {truncate(product.title, { length: 36 })}
              </span>

              <div className="tw-flex tw-gap-2 tw-items-center">
                <span className="tw-font-normal tw-text-sm tw-text-gray-950">
                  {product.currency}
                  {formatMoney(
                    hasDiscount(selectedProductToSend || product)
                      ? (selectedProductToSend?.discounted_price || product.discounted_price)
                      : (selectedProductToSend?.price || product.price)
                  )}
                </span>
                {hasDiscount(selectedProductToSend || product) && (
                  <span className="tw-line-through tw-text-xs tw-text-gray-400">
                    {product.currency}
                    {formatMoney(selectedProductToSend?.price || product.price)}
                  </span>
                )}
                <span className="tw-font-normal tw-text-sm tw-text-gray-700 tw-ml-auto">
                  {selectedProductToSend?.quantity || product.quantity}
                  {' '}
                  stock
                </span>
              </div>

              {/* Descripción del producto */}
              <ProductDescription product={product} />

              {/* Variantes del producto */}
              {!isEmpty(product.product_variant_combinations) && (
                <ProductVariantsComponent
                  product={product}
                  setSelectedProductToSend={setSelectedProductToSend}
                />
              )}

              {/* Galería de imágenes */}
              {!isEmpty(selectedProductToSend)
               && !selectedProductToSend?.is_combination
               && !isEmpty(selectedProductToSend?.image_urls)
               && selectedProductToSend.image_urls.length > 1 && (
               <>
                 <span className="tw-font-semibold tw-text-gray-950 tw-text-sm tw-mb-2.5">
                   {t('productDetail.gallery')}
                 </span>

                 <div className="tw-flex tw-flex-wrap" style={{ gap: '10px' }}>
                   {selectedProductToSend.image_urls.map((img) => (
                     <div
                       key={`p-image-${img.id}`}
                       className="tw-p-1 tw-bg-gray-100 tw-border-1 tw-border-gray-200 tw-rounded-lg"
                     >
                       <img
                         src={img.url}
                         referrerPolicy="no-referrer"
                         alt="Producto"
                         className="tw-rounded-lg tw-object-cover"
                         style={{ width: '110px', height: '90px' }}
                       />
                     </div>
                   ))}
                 </div>
               </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductDetail.displayName = 'ProductDetail';

export default ProductDetail;
